import React, { Fragment, useRef, useState, useEffect } from 'react';
import Button from '../button/Button';
import DragHandle from './DragHandle';
import './Modal.css';
import ModalBackdrop from './modalBackdrop/ModalBackdrop';
import { useModal } from '../../context/ModalContext';

interface Props {
  isOpen?: boolean;
  children?: React.ReactNode;
  ModalRef?: any;
  title?: any;
  onClose?: () => void;
  allowButtonClosed?: boolean;
  textEnterButton?: string;
  handleEnterButton?: () => void;
  messageModal?: string;
  isLoadingEnterButton?: boolean;
  closeButtonText?: string;
  styleEnterButton?: React.CSSProperties;
  imageEnterButton?: React.ReactNode;
  disableEnterButton?: boolean;
  dragHandle?: boolean;
  modalBackdrop?: boolean;
  header?: boolean;
}

const Modal: React.FC<Props> = ({
  isOpen,
  children,
  closeButtonText = 'Fechar',
  ModalRef,
  imageEnterButton,
  styleEnterButton,
  isLoadingEnterButton = false,
  textEnterButton = '',
  messageModal,
  title,
  onClose,
  allowButtonClosed = false,
  handleEnterButton,
  disableEnterButton = false,
  dragHandle = true,
  modalBackdrop = true,
  header = true,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [isClosing, setIsClosing] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const { setModalOpen } = useModal();

  const touchStartY = useRef<number | null>(null);

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    if (touchStartY.current === null) return;
    const touchEndY = e.changedTouches[0].clientY;
    const diffY = touchEndY - touchStartY.current;
    if (diffY > 60) {
      handleClose();
    }
    touchStartY.current = null;
  };

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setModalOpen(true);
    } else {
      // Delay hiding to allow animation to complete
      const timer = setTimeout(() => {
        setShouldRender(false);
        setModalOpen(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen, setModalOpen]);

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    e.dataTransfer.setData('text/plain', ''); // Necessary for Firefox to allow dragging
    e.dataTransfer.setDragImage(document.createElement('div'), 0, 0);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    handleClose();
  };

  const handleClose = () => {
    if (isClosing) return;

    setIsClosing(true);

    if (onClose) onClose();
    setIsClosing(false);
  };

  if (!shouldRender) return null;

  return (
    <>
      {modalBackdrop && <ModalBackdrop onClick={handleClose} />}

      <div
        className={`modal-pattern-container ${isOpen ? 'active' : ''} ${
          isClosing ? 'closing' : ''
        }`}
        ref={ModalRef}
        aria-modal='true'
        draggable={false}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        style={{
          transform: `translateY(${isOpen ? 0 : '100%'})`,
          transition: 'transform 0.4s',
          width: '100%',
        }}
      >
        <div
          className={`modal-pattern-content ${isOpen ? 'active' : ''}`}
          ref={modalRef}
        >
          {dragHandle && (
            <DragHandle
              onClose={handleClose}
              disabled={!onClose || isClosing}
            />
          )}
          {header && (
            <div
              className='modal-pattern-header'
              style={!dragHandle ? { paddingTop: 40 } : {}}
            >
              {title || messageModal ? (
                <div className='modal-pattern-header-title' tabIndex={0}>
                  {typeof title === 'function' ? title() : title || messageModal}
                </div>
              ) : null}
              {title && messageModal ? (
                <div className='modal-pattern-header-message'>{messageModal}</div>
              ) : null}
            </div>
          )}
          <div className='modal-pattern-body'>
            <div>{children}</div>

            {textEnterButton && (
              <Button
                style={
                  styleEnterButton
                    ? {
                        ...styleEnterButton,
                        opacity: disableEnterButton ? 0.5 : 1,
                        ...(disableEnterButton && {
                          backgroundColor: '#787E7D',
                        }),
                      }
                    : {
                        backgroundColor: '#fff',
                        color: '#787E7D',
                        border: '1px solid #787E7D',
                        opacity: disableEnterButton ? 0.5 : 1,
                      }
                }
                type='button'
                children={textEnterButton}
                onClick={handleEnterButton}
                spinnerSecondary={true}
                loading={isLoadingEnterButton}
                image={imageEnterButton}
                disabled={disableEnterButton}
              />
            )}
            {allowButtonClosed && (
              <Button
                style={{
                  backgroundColor: '#fff',
                  color: '#787E7D',
                  border: textEnterButton ? 'none' : '1px solid #787E7D',
                }}
                type='button'
                children={closeButtonText}
                onClick={handleClose} // Mesmo evento usado em todos os lugares
                className='valet-close-btn'
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Modal;
